import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function Methode() {
  return (
    <div className="methode-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="section-container">
          <span className="section-label">Méthode</span>
          <h1 className="page-title">Notre approche</h1>
          <p className="page-subtitle">
            Open Cortex intervient selon une méthode structurée, éprouvée sur le terrain et adaptée à chaque contexte. Cette approche permet de clarifier le fonctionnement réel d'une organisation, d'en structurer les processus clés et d'identifier les leviers d'amélioration prioritaires. L'objectif : donner aux dirigeants les moyens de piloter leur évolution opérationnelle avec clarté et continuité.
          </p>
        </div>
      </section>

      {/* Principles Section */}
      <section className="principles-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Principes directeurs</h2>
            <p className="section-description">
              Notre méthode repose sur quatre piliers qui garantissent la pertinence et la durabilité de nos interventions.
            </p>
          </div>
          <div className="principles-grid">
            <div className="principle-card">
              <h3>Observer le fonctionnement réel</h3>
              <p>
                Nous analysons les opérations telles qu'elles se déroulent au quotidien, dans leurs interactions concrètes et leurs contraintes effectives. Cette observation directe permet d'identifier ce qui structure réellement l'activité et d'éviter les écarts entre le prescrit et le pratiqué.
              </p>
            </div>
            <div className="principle-card">
              <h3>Formaliser avec méthode</h3>
              <p>
                Nous documentons les processus de manière rigoureuse, structurée et exploitable. La formalisation vise à créer une base de travail claire, partagée et maintenable, permettant de prendre des décisions sur des fondements solides et de faciliter la transmission des savoir-faire.
              </p>
            </div>
            <div className="principle-card">
              <h3>Ajuster de manière continue</h3>
              <p>
                Nous intégrons des points de validation réguliers tout au long de l'intervention. Cette logique itérative permet d'ajuster le cadre de travail en fonction des retours terrain, d'affiner les priorités et de maintenir l'alignement avec les objectifs opérationnels.
              </p>
            </div>
            <div className="principle-card">
              <h3>Assurer l'autonomie</h3>
              <p>
                Nous transférons systématiquement les compétences nécessaires pour maintenir et faire évoluer ce qui a été structuré. Cette autonomisation permet de garantir la continuité après notre départ et de renforcer la capacité interne à piloter les évolutions futures.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="section-container">
          <div className="section-header">
            <h2 className="section-title">Les étapes de l'intervention</h2>
            <p className="section-description">
              Notre méthode suit un déroulement structuré en quatre phases principales, adaptées selon le périmètre et les objectifs de chaque mission. Ce cadre méthodologique s'ajuste à chaque contexte : le périmètre d'intervention, les étapes activées et les livrables associés sont précisés dans le devis.
            </p>
          </div>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-marker">
                <div className="step-number">1</div>
                <div className="step-line"></div>
              </div>
              <div className="step-content">
                <h3>Diagnostic</h3>
                <div className="step-details">
                  <h4>Objectif</h4>
                  <p>Cartographier le fonctionnement opérationnel réel, identifier les points structurants et les zones de tension, établir une hiérarchie des leviers d'amélioration.</p>
                  <h4>Méthode</h4>
                  <p>Observation directe des opérations, entretiens avec les parties prenantes clés, analyse de la documentation existante, identification des flux critiques et des dépendances.</p>
                  <h4>Livrables</h4>
                  <p>Cartographie des processus clés, identification des points d'attention prioritaires, axes d'action hiérarchisés pour orienter la suite de l'intervention.</p>
                </div>
              </div>
            </div>

            <div className="process-step">
              <div className="step-marker">
                <div className="step-number">2</div>
                <div className="step-line"></div>
              </div>
              <div className="step-content">
                <h3>Conception</h3>
                <div className="step-details">
                  <h4>Objectif</h4>
                  <p>Concevoir les solutions opérationnelles adaptées au contexte : structuration des processus, automatisation de workflows ciblés, intégration d'usages IA pertinents.</p>
                  <h4>Méthode</h4>
                  <p>Co-construction avec les équipes opérationnelles, prototypage des solutions envisagées, validation itérative des choix, ajustement en fonction des retours terrain.</p>
                  <h4>Livrables</h4>
                  <p>Spécifications fonctionnelles des processus restructurés, schémas de workflows automatisés, cadre d'usage des solutions IA, recommandations d'outillage si nécessaire.</p>
                </div>
              </div>
            </div>

            <div className="process-step">
              <div className="step-marker">
                <div className="step-number">3</div>
                <div className="step-line"></div>
              </div>
              <div className="step-content">
                <h3>Formalisation</h3>
                <div className="step-details">
                  <h4>Objectif</h4>
                  <p>Documenter l'ensemble des processus, workflows et solutions conçues de manière exploitable au quotidien, garantir la clarté et la maintenabilité dans la durée.</p>
                  <h4>Méthode</h4>
                  <p>Rédaction de fiches de procédures opérationnelles, documentation technique des automatisations, création de supports de référence, structuration des connaissances clés.</p>
                  <h4>Livrables</h4>
                  <p>Documentation complète et utilisable, fiches de procédures formalisées, guides de maintenance des automatisations, cadre de pilotage des évolutions.</p>
                </div>
              </div>
            </div>

            <div className="process-step">
              <div className="step-marker">
                <div className="step-number">4</div>
              </div>
              <div className="step-content">
                <h3>Transfert</h3>
                <div className="step-details">
                  <h4>Objectif</h4>
                  <p>Transférer les compétences nécessaires pour maintenir, faire évoluer et piloter les processus et solutions mis en place, garantir l'autonomie opérationnelle.</p>
                  <h4>Méthode</h4>
                  <p>Formation des équipes aux nouveaux processus, accompagnement sur les premiers usages, transfert des méthodes de maintenance et d'évolution, mise en place d'indicateurs de suivi.</p>
                  <h4>Livrables</h4>
                  <p>Équipes formées et autonomes, documentation de référence transférée, indicateurs de pilotage opérationnels, plan de continuité établi.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Automation & AI Section */}
      <section className="automation-section">
        <div className="section-container">
          <div className="automation-content">
            <h2>Place de l'automatisation et de l'intelligence artificielle</h2>
            <p className="lead-text">
              L'automatisation et l'IA sont des leviers puissants lorsqu'ils s'appuient sur des processus structurés et des besoins clairement identifiés. Nous les intégrons de manière ciblée, après avoir clarifié le fonctionnement existant et déterminé où leur usage apporte une valeur mesurable.
            </p>
            <div className="automation-benefits">
              <div className="benefit-item">
                <h4>Coordination opérationnelle</h4>
                <p>Automatisation des flux entre étapes, priorisation des actions, continuité en cas d'absence. L'objectif : fluidifier les opérations et réduire les frictions.</p>
              </div>
              <div className="benefit-item">
                <h4>Gain de temps décisionnel</h4>
                <p>Libération du temps consacré aux tâches répétitives, réallocation vers des activités à plus fort impact, capacité accrue à traiter des volumes croissants.</p>
              </div>
              <div className="benefit-item">
                <h4>Aide à la décision</h4>
                <p>Classification automatisée, analyse de données, qualification des informations. L'IA intervient comme support à la décision, dans un cadre maîtrisé et documenté.</p>
              </div>
              <div className="benefit-item">
                <h4>Fiabilité et traçabilité</h4>
                <p>Processus documentés et automatisés, réduction des erreurs, continuité opérationnelle renforcée, capacité à maintenir et faire évoluer les solutions dans la durée.</p>
              </div>
            </div>
            <p className="automation-note">
              Ces leviers sont activés après la phase de structuration. Une base claire permet des automatisations fiables, maintenables et alignées avec les priorités opérationnelles.
            </p>
          </div>
        </div>
      </section>

      {/* Collaboration Section */}
      <section className="collaboration-section">
        <div className="section-container">
          <div className="collaboration-content">
            <h2>Une collaboration structurée</h2>
            <p className="lead-text">
              Nos interventions reposent sur un cadre de travail explicite, des échanges réguliers et une capacité à ajuster le cap en fonction des réalités terrain. Cette clarté initiale permet un pilotage serein de la mission et une prise de décision éclairée à chaque étape.
            </p>
            <p>
              Chaque intervention est formalisée par un devis détaillant le périmètre, les livrables attendus, les étapes clés et les modalités d'échange. L'objectif : établir un cadre de collaboration adulte, transparent et orienté résultats opérationnels durables.
            </p>
            <p>
              À l'issue de la mission, vous disposez de l'ensemble des livrables, de la documentation structurée et des compétences transférées pour maintenir et faire évoluer ce qui a été mis en place. Cette autonomie est au cœur de notre approche.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Discuter de votre contexte</h2>
            <p>Un premier échange permet de clarifier vos besoins et de déterminer le cadre d'intervention adapté.</p>
            <Link to="/contact">
              <Button className="btn-primary btn-large">
                Prendre contact <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
